/* ───────────────────────────────────────────────────────────────────────────
 * staticNotificationsData.js
 *
 * Frontend-only static fixtures for the Notifications module on the
 * 3PL Logistics static dashboard branch.
 *
 * The whole notifications module on this branch is STATIC — no backend / API
 * calls are made anywhere. All notification action-creators read from this
 * file and dispatch the data after a short (1s) delay so the existing loaders
 * still show. Event names are stored as their camelCase codes (exactly like
 * the real app) so the components' acronym-aware `formatEventName` renders them
 * nicely (e.g. `aboRiskChanged` → "ABO Risk Changed").
 * ─────────────────────────────────────────────────────────────────────────── */

// Top unread-count badge (static — never fetched). Decreases as rows are read.
export const STATIC_UNREAD_COUNT = 10;

// Category enums (drive the feed "Filter by category" + the manage-screen tabs).
// "Others" intentionally excluded per request.
export const STATIC_CATEGORY_ENUMS = [
  'PO_MANAGEMENT',
  'BATCH_MANUFACTURING',
  'BATCH_ALLOCATION_BY_BMS',
  'BATCH_ALLOCATION_BY_CMO',
  'INVENTORY_RECONCILIATION',
  'USER_MANAGEMENT',
];

// Master event catalog (camelCase code → category). Real event names.
const EVENT_CATALOG = {
  PO_MANAGEMENT: [
    'poCreation', 'sharedWithCMO', 'poUpdateToSAP', 'cmoSendsDeliveryQuantityProposal',
    'bmsSendsCounterProposal', 'bmsAcceptsCMOProposal', 'cmoAcceptsBMSCounterProposal',
    'artworkReferenceNumberChanged', 'artworkDocumentUploaded', 'aboRiskChanged',
    'statDateNotUpdatedPORejected', 'euImportationSamplingMarkedAsNotRequired',
    'euImportationSamplingMarkedAsRequired', 'euImportationSamplingQtyAdded',
    'euImportationSamplingQtyChanged', 'cmoProvidesSalesOrderNumber', 'aboRiskAdded',
    'artworkReferenceNumberAdded', 'bmsAddsPOLevelInfo', 'cmoAcknowledgesPO',
    'stabilitySamplingMarkedAsRequired', 'stabilitySamplingMarkedAsNotRequired',
    'stabilitySamplingQtyChanged', 'stabilitySamplingQtyAdded', 'stabilitySamplingFormNumberAdded',
    'stabilitySamplingFormNumberChanged', 'stabilitySamplingFormNumberDocumentUploaded',
    'newArtworkMarkedAsNotRequired', 'newArtworkMarkedAsRequired', 'cmoProposalWithinAcceptanceLimit',
    'bmsRejectsCMOProposal', 'iterativeNegotiationInProgress', 'poValuesConfirmed',
    'statDateUpdatedPOAccepted',
  ],
  BATCH_MANUFACTURING: [
    'preShipmentDocumentsUploaded', 'endEvent', 'batchStatusEvent', 'batchExpiredExtendExpiryDateEvent',
    'rejectedQtyVarianceAndBatchMismatch', 'approvedQtyVarianceAndBatchMismatch',
    'approvalForQtyVarianceAndBatchMismatch', 'postShipmentDocumentsUploaded', 'needDocumentsReview',
    'fieldReviewedByUser', 'asnGenerated', 'productReleaseOrRejected', 'corruptedOrInvalidFilesEvent',
    'ASLFetchFailed', 'ASLCheckReportandGRNFailure', 'ASLCheckReportandGRN', 'IBDCreationFailure',
    'batchScheduled', 'postMfgDocumentsUploaded', 'consumedBatchExpiry2ndTime', 'IBDValidationFailure',
    'postShipmentDataExtracted', 'endOfBatchManufacturing', 'materialRejection', 'stockCheckNeeded',
    'approvalRequiredVarianceMismatchDetected', 'preShipmentDataExtracted', 'qaCheckNeeded',
    'validationFailureConsumedMaterialBatch', 'fieldReviewByUserPostShip', 'fieldReviewByUserPostMfg',
    'fieldReviewByUserPreShip', 'digitizedDocsNeedReview', 'documentResubmissionRequested',
    'partialGRNorIBDGenerated', 'partialIBDGenerated', 'partialGRNGenerated', 'fullQtyGRNGenerated',
    'fullQtyIBDGenerated', 'validationFailurePreIBDChecks', 'validationFailurePreGRNChecks',
    'ASLDetailsNotFound', 'ASLExpired',
  ],
  BATCH_ALLOCATION_BY_BMS: [
    'lotAllocatedForPOBySponsor', 'lotAllocationEditedForPOBySponsor', 'componentAllocationBOMChange',
    'reviewBatchAllocation', 'changeDeliveryDate', 'componentAllocationUpdatedNoApprovalRequired',
    'componentAllocationApprovalRequested', 'componentAllocationApprovalAccepted',
    'componentAllocationApprovalRejected', 'componentAllocationAllApprovalsDone', 'componentDepletedBatch',
    'allocationDataPreparedForSAP', 'orderIdentifiedForCMOAllocation', 'singleAllocation',
    'multipleAllocation', 'singleBatchOverAllocationBMS', 'singleBatchUnderAllocationBMS',
    'multipleBatchesOverAllocationBMS', 'multipleBatchesUnderAllocationBMS', 'componentAllocationDeleted',
    'netAvailableQtyBecomesNegative', 'plannedMaterialBatchAllocated', 'batchAllocationInitiated',
    'autoAllocationSuccessful', 'autoAllocationFailed',
  ],
  BATCH_ALLOCATION_BY_CMO: [
    'cmoAllocatesPlannedConsumedBatch', 'lotAllocationEditedForPOByCMO', 'multipleBatchesUnderAllocationByCMO',
    'allocatedBatchReflectedInInventory', 'singleBatchOverAllocationCMO', 'singleBatchUnderAllocationCMO',
    'multipleBatchesOverAllocationCMO', 'multipleBatchesUnderAllocationCMO',
  ],
  INVENTORY_RECONCILIATION: [
    'fetchCMOInventoryData', 'bmsUserReviewsDiscrepancies',
  ],
  USER_MANAGEMENT: [
    'userRoleChange',
  ],
};

// Flatten the catalog into events with stable sequential IDs (category order preserved).
const STATIC_EVENTS = [];
{
  let id = 1;
  STATIC_CATEGORY_ENUMS.forEach((cat) => {
    (EVENT_CATALOG[cat] || []).forEach((code) => {
      STATIC_EVENTS.push({ eventTypeID: id, code, category: cat });
      id += 1;
    });
  });
}

const LAST_UPDATED_BY = 'Alisson34.Taylor@maildrop.cc';
const LAST_UPDATED_DATE = '2026-05-13T10:24:00.000Z';

// ── Org / Dept / Role lookup (edit popup dropdowns) ─────────────────────────
export const STATIC_ORG_DEPT_ROLES = {
  organizations: [
    { organization_name: 'PCI', organization_id: 'ORG_PCI', orgType: 'CMO' },
    { organization_name: 'BMS (3200)', organization_id: 'ORG_BMS', orgType: 'BMS' },
    { organization_name: 'Patheon', organization_id: 'ORG_PATHEON', orgType: 'CMO' },
  ],
  roles: [
    { role_name: 'Admin', collaboratorRoleName: 'Admin' },
    { role_name: 'Operation Supply Planner (FIN)', collaboratorRoleName: 'Operation Supply Planner (FIN)' },
    { role_name: 'BMS EXM SC VPT Lead', collaboratorRoleName: 'BMS EXM SC VPT Lead' },
    { role_name: 'Global Supply Chain Planning Manager', collaboratorRoleName: 'Global Supply Chain Planning Manager' },
  ],
  departments: [
    { departmentName: 'Administration' },
    { departmentName: 'GSCP' },
    { departmentName: 'BMS GSCP' },
    { departmentName: 'GSCM' },
  ],
};

// ── Role rows for the eye/edit popup — SAME static data for every event ─────
export const STATIC_EVENT_ROLES = [
  { eventToRoleMappingID: 1, collaboratorRoleID: 101, collaboratorRoleName: 'Admin', departmentName: 'Administration', vendorMosymphonyID: 'ORG_PCI', actionRequired: true },
  { eventToRoleMappingID: 2, collaboratorRoleID: 102, collaboratorRoleName: 'Operation Supply Planner (FIN)', departmentName: 'GSCP', vendorMosymphonyID: 'ORG_BMS', actionRequired: true },
  { eventToRoleMappingID: 3, collaboratorRoleID: 103, collaboratorRoleName: 'BMS EXM SC VPT Lead', departmentName: 'BMS GSCP', vendorMosymphonyID: 'ORG_BMS', actionRequired: false },
  { eventToRoleMappingID: 4, collaboratorRoleID: 104, collaboratorRoleName: 'Admin', departmentName: 'Administration', vendorMosymphonyID: 'ORG_PATHEON', actionRequired: true },
  { eventToRoleMappingID: 5, collaboratorRoleID: 105, collaboratorRoleName: 'Admin', departmentName: 'GSCM', vendorMosymphonyID: 'ORG_BMS', actionRequired: false },
  { eventToRoleMappingID: 6, collaboratorRoleID: 106, collaboratorRoleName: 'Global Supply Chain Planning Manager', departmentName: 'GSCP', vendorMosymphonyID: 'ORG_BMS', actionRequired: true },
];

// ── Categories (for /fetchNotificationCategories) ───────────────────────────
export function getStaticCategories() {
  return [...STATIC_CATEGORY_ENUMS];
}

// ── Manage Notifications preferences (Dashboard checked, rest unchecked) ────
// Shape the FETCH_PREFERENCES reducer expects: groups of
// { objectType, eventNotifications: [{ eventToUserMappingID, eventTypeID, eventType, dashboardNotification, emailNotification, lastUpdatedDate }] }
export function getStaticPreferenceGroups() {
  return STATIC_CATEGORY_ENUMS.map((cat) => ({
    objectType: cat,
    eventNotifications: STATIC_EVENTS
      .filter((e) => e.category === cat)
      .map((e) => ({
        eventToUserMappingID: e.eventTypeID,
        eventTypeID: e.eventTypeID,
        eventType: e.code,
        eventDescription: '',
        dashboardNotification: true,
        emailNotification: false,
        whatsappNotification: false,
        smsNotification: false,
        automatedCallNotification: false,
        lastUpdatedDate: LAST_UPDATED_DATE,
      })),
  }));
}

// ── Manage Distribution List (events + last updated + roles) ────────────────
// Shape the FETCH_DISTRIBUTION reducer expects:
// { eventNotificationList: [{ objectType, eventTypes: [{ eventTypeID, eventTypeName, lastUpdatedBy, lastUpdatedDate, <restrict flags>, roles }] }] }
export function getStaticDistributionResponse() {
  return {
    status: true,
    eventNotificationList: STATIC_CATEGORY_ENUMS.map((cat) => ({
      objectType: cat,
      eventTypes: STATIC_EVENTS
        .filter((e) => e.category === cat)
        .map((e) => ({
          eventTypeID: e.eventTypeID,
          eventTypeName: e.code,
          eventDescription: '',
          brandUserAssociationReqd: true,
          cmoUserAssociationReqd: true,
          productTypeUserAssociationReqd: true,
          lastUpdatedBy: LAST_UPDATED_BY,
          lastUpdatedDate: LAST_UPDATED_DATE,
          roles: STATIC_EVENT_ROLES,
        })),
    })),
  };
}

// ── Main feed ───────────────────────────────────────────────────────────────
// Each row carries full expanded-detail values (no "NA"). PO / product / batch
// data is taken from the shared screenshots; Organization is always "BMS".
const CATEGORY_TO_OBJECT_TYPE = {
  PO_MANAGEMENT: 'PO',
  BATCH_MANUFACTURING: 'BATCH',
  BATCH_ALLOCATION_BY_BMS: 'BATCH_ALLOCATION',
  BATCH_ALLOCATION_BY_CMO: 'BATCH_ALLOCATION',
  INVENTORY_RECONCILIATION: 'INVENTORY',
  USER_MANAGEMENT: 'USER',
};

const FEED_DEFS = [
  { code: 'allocationDataPreparedForSAP', category: 'BATCH_ALLOCATION_BY_BMS', poNumber: '4500006500', productCode: '2451', productDescription: 'BARACLUDE TAB 0.5MG (3BLP X 10) FR', poStatus: 'Confirmed', batchStatus: 'NA', batchNumber: 'ENTCBD10', cmoName: 'PCI', roleName: 'Admin', username: 'Alisson Taylor', daysAgo: 1 },
  { code: 'fieldReviewByUserPostMfg', category: 'BATCH_MANUFACTURING', poNumber: '4500006215', productCode: '2451', productDescription: 'BARACLUDE TAB 0.5MG (3BLP X 10) FR', poStatus: 'Confirmed', batchStatus: 'Specs Approved', batchNumber: 'PQR6215', cmoName: 'Patheon', roleName: 'Operation Supply Planner (FIN)', username: 'charan', daysAgo: 1 },
  { code: 'documentResubmissionRequested', category: 'BATCH_MANUFACTURING', poNumber: '4500004614', productCode: '575432', productDescription: 'Trastuzumab', poStatus: 'In Progress', batchStatus: 'Post Manufacturing Digitalized Data Validated', batchNumber: 'TR04614', cmoName: 'Oxford Biomedica', roleName: 'Admin', username: 'Alisson Taylor', daysAgo: 2 },
  { code: 'approvalRequiredVarianceMismatchDetected', category: 'BATCH_MANUFACTURING', poNumber: '4500006103', productCode: '2388', productDescription: 'CEFF DRY SYRUP (30ML)(SFG)', poStatus: 'Changes under review', batchStatus: 'Shipment Authorized', batchNumber: 'PQR6103', cmoName: 'Oxford Biomedica', roleName: 'Operation Supply Planner (FIN)', username: 'charan', daysAgo: 2 },
  { code: 'endOfBatchManufacturing', category: 'BATCH_MANUFACTURING', poNumber: '4500003376', productCode: '2340', productDescription: 'Entecavir Tab 0.5 mg Bulk Drum PATH(SFG) IE', poStatus: 'In Progress', batchStatus: 'Post-Shipment Digitalized Data validated', batchNumber: 'C03376', cmoName: 'Oxford Biomedica', roleName: 'Admin', username: 'Alisson Taylor', daysAgo: 3 },
  { code: 'digitizedDocsNeedReview', category: 'BATCH_MANUFACTURING', poNumber: '4500003378', productCode: '2340', productDescription: 'Entecavir Tab 0.5 mg Bulk Drum PATH(SFG) IE', poStatus: 'In Progress', batchStatus: 'Post-Shipment Digitalized Data validated', batchNumber: 'C03378', cmoName: 'Oxford Biomedica', roleName: 'Operation Supply Planner (FIN)', username: 'charan', daysAgo: 3 },
  { code: 'lotAllocatedForPOBySponsor', category: 'BATCH_ALLOCATION_BY_BMS', poNumber: '4500004614', productCode: '575432', productDescription: 'Trastuzumab', poStatus: 'In Progress', batchStatus: 'Post Manufacturing Digitalized Data Validated', batchNumber: 'TR04614', cmoName: 'Oxford Biomedica', roleName: 'Admin', username: 'Alisson Taylor', daysAgo: 4 },
  { code: 'cmoAllocatesPlannedConsumedBatch', category: 'BATCH_ALLOCATION_BY_CMO', poNumber: '4500006103', productCode: '2388', productDescription: 'CEFF DRY SYRUP (30ML)(SFG)', poStatus: 'Changes under review', batchStatus: 'Shipment Authorized', batchNumber: 'PQR6103', cmoName: 'Oxford Biomedica', roleName: 'Operation Supply Planner (FIN)', username: 'charan', daysAgo: 4 },
  { code: 'euImportationSamplingMarkedAsRequired', category: 'PO_MANAGEMENT', poNumber: '4500003376', productCode: '2340', productDescription: 'Entecavir Tab 0.5 mg Bulk Drum PATH(SFG) IE', poStatus: 'In Progress', batchStatus: 'Post-Shipment Digitalized Data validated', batchNumber: 'C03376', cmoName: 'Oxford Biomedica', roleName: 'Admin', username: 'Alisson Taylor', daysAgo: 5 },
  { code: 'fetchCMOInventoryData', category: 'INVENTORY_RECONCILIATION', poNumber: '4500003378', productCode: '2340', productDescription: 'Entecavir Tab 0.5 mg Bulk Drum PATH(SFG) IE', poStatus: 'In Progress', batchStatus: 'Post-Shipment Digitalized Data validated', batchNumber: 'C03378', cmoName: 'Oxford Biomedica', roleName: 'Operation Supply Planner (FIN)', username: 'charan', daysAgo: 5 },
];

// Build the feed rows once; ids are stable so read/star state can persist.
const FEED_ROWS = FEED_DEFS.map((def, idx) => {
  const notifiedAt = new Date(Date.now() - def.daysAgo * 86400000).toISOString();
  return {
    user_event_id: 9000 + idx,
    title: def.code,
    event_category: def.category,
    object_type: CATEGORY_TO_OBJECT_TYPE[def.category] || 'OTHER',
    po_number: def.poNumber,
    product_code: def.productCode,
    product_description: def.productDescription,
    batch_number: def.batchNumber,
    vendor_mosymphony_id: '1000549',
    cmoName: 'Oxford Biomedica',
    organizationName: 'BMS',
    roleName: def.roleName,
    notified_at: notifiedAt,
    is_read: false,
    is_starred: false,
    has_action_link: false,
    created_by: def.username,
    payload: {
      poNumber: def.poNumber,
      productCode: def.productCode,
      productDescription: def.productDescription,
      poStatus: def.poStatus,
      statusDisplayName: def.poStatus,
      batchStatus: def.batchStatus,
      batchNumber: def.batchNumber,
      lastUpdatedByName: def.username,
    },
  };
});

export const STATIC_FEED_IDS = FEED_ROWS.map((r) => r.user_event_id);

// Read / star state persists across re-fetches (filter changes) within a session.
const _readIds = new Set();
const _starredIds = new Set();

export function markStaticRead(id) {
  _readIds.add(id);
}
export function markAllStaticRead() {
  STATIC_FEED_IDS.forEach((id) => _readIds.add(id));
}
export function toggleStaticStar(id) {
  if (_starredIds.has(id)) {
    _starredIds.delete(id);
    return false;
  }
  _starredIds.add(id);
  return true;
}

// Live unread count = total feed rows minus those already read (chevron-expanded).
export function getStaticUnreadCount() {
  return Math.max(0, STATIC_FEED_IDS.length - _readIds.size);
}

// Returns a feed page honoring the same filter params the real endpoint uses.
export function getStaticNotificationsResponse(params = {}) {
  let rows = FEED_ROWS.map((r) => ({
    ...r,
    is_read: _readIds.has(r.user_event_id),
    is_starred: _starredIds.has(r.user_event_id),
  }));

  if (params.is_read === true) rows = rows.filter((r) => r.is_read);
  else if (params.is_read === false) rows = rows.filter((r) => !r.is_read);

  if (params.starred === true) rows = rows.filter((r) => r.is_starred);
  else if (params.starred === false) rows = rows.filter((r) => !r.is_starred);

  if (Array.isArray(params.categories) && params.categories.length) {
    rows = rows.filter((r) => params.categories.includes(r.event_category));
  }

  if (params.search) {
    const q = String(params.search).toLowerCase();
    rows = rows.filter(
      (r) =>
        (r.title || '').toLowerCase().includes(q) ||
        (r.po_number || '').toLowerCase().includes(q) ||
        (r.product_description || '').toLowerCase().includes(q)
    );
  }

  return {
    status: true,
    data: rows,
    total: rows.length,
    page: 1,
    pageSize: params.page_size || rows.length || 10,
  };
}
